import { useState, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { BannerProps } from "@/components/wpl/banner-alert/src";
import { Link } from "@/libraries/design/components";
import { Cross } from "@/libraries/wpl/foundations/src";

export type TeamTourBannerProps = BannerProps & {
    lockupImageSrc: string;
    mobileHeaderText: string;
};

export const TeamTourBannerAlert = ({
    identifier,
    text,
    linkText,
    linkUrl,
    dataEventProps,
    lockupImageSrc,
    mobileHeaderText
}: TeamTourBannerProps) => {
    const intl = useIntl();
    const bannerStorageItem = `BannerAlert_dismiss-${identifier}`;

    const lockupImage = `url("${lockupImageSrc}") no-repeat 100% 100% / contain`;

    const containerWrapperStyles = css({
        backgroundColor: "#E9F2FE",
        position: "relative",
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        borderBlockEnd: "2px solid #1868DB",
        [`&[data-dismissed="true"]`]: {
            display: "none"
        },
        ["@media (min-width: 1273px)"]: {
            // Custom breakpoint needed for text wrapping
            maxHeight: "144px"
        }
    });

    const containerStyles = css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "24px 24px 24px 24px"
    });

    const contentWrapperStyles = css({
        display: "grid",
        justifyContent: "center",
        justifyItems: "center",
        alignContent: "center",
        alignItems: "center",
        gap: "32px",
        width: "100%",
        ["@media (min-width: 1273px)"]: {
            justifyContent: "start"
        },
        [media.above.xl]: {
            justifyContent: "center"
        }
    });

    const contentStyles = css({
        gridRow: "1",
        display: "inline-flex",
        flexDirection: "column",
        justifyContent: "start",
        justifyItems: "center",
        alignContent: "start",
        alignItems: "start",
        gap: "8px",
        width: "100%",
        ["@media (min-width: 1273px)"]: {
            display: "grid",
            justifyContent: "start",
            justifyItems: "center",
            alignContent: "center",
            alignItems: "center",
            gap: "32px"
        },
        [media.above.xl]: {
            display: "grid",
            justifyContent: "center",
            justifyItems: "center",
            alignContent: "center",
            alignItems: "center",
            gap: "32px"
        }
    });

    const backgroundShapeStyles = css({
        gridRow: "1",
        zIndex: "1",
        width: "147px",
        height: "142px",
        background:
            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/team-tour-background-new.webp) no-repeat",
        backgroundSize: "cover",
        alignSelf: "end",
        display: "none",
        ["@media (min-width: 1273px)"]: {
            display: "block"
        }
    });

    const lockupStyles = css({
        gridRow: "1",
        width: "160px",
        height: "48px",
        background: lockupImage,
        [media.below.sm]: {
            display: "none"
        },
        [media.above.sm]: {
            display: "block"
        },
        ["@media (min-width: 1273px)"]: {
            display: "block"
        }
    });

    const dividerStyles = css({
        backgroundColor: "#6C6F77",
        display: "none",
        height: "100%",
        width: "1px",
        gridRow: "1",
        [media.above.sm]: {
            maxHeight: "180px",
            display: "block"
        },
        ["@media (min-width: 1273px)"]: {
            maxHeight: "96px"
        }
    });

    const textStyles = css({
        gridRow: "1",
        color: "#091e42",
        font: "var(--bxp-charlie-text-font-family)",
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: "400",
        marginBlockStart: "0",
        width: "100%",
        maxWidth: "100%",
        ["@media (min-width: 1273px)"]: {
            marginBlockStart: "revert",
            maxHeight: "96px"
        }
    });

    const linkStyles = css({
        "gridRow": "1",
        "height": "hug(20px)",
        "font-family": "var(--bxp-charlie-text-font-family)",
        "font-size": "16px",
        "line-height": "20px",
        "text-align": "left",
        "color": "#1868DB !important",
        "text-decoration": "none !important",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "a": {
            "text-decoration": "none !important",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "svg": {
                transition: "transform 0.25s ease-in-out"
            }
        },
        "&:hover": {
            color: "#0065ff !important"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "a:hover": {
            textDecoration: "none",
            textShadow: "none",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            svg: {
                transform: `translateX(${token("space.075")})`
            }
        }
    });

    const fillStyles = css({
        display: "none",
        ["@media (min-width: 1273px)"]: {
            display: "block",
            gridRow: "1",
            width: "24px",
            height: "48px"
        }
    });

    const closeIconStyles = css({
        gridRow: "1",
        appearance: "none",
        background: "transparent",
        border: 0,
        cursor: "pointer",
        padding: 0,
        alignSelf: "baseline",
        width: "16px",
        marginInlineStart: "-16px",
        [media.above.sm]: {
            marginInlineStart: "0px"
        },
        ["@media (min-width: 1273px)"]: {
            alignSelf: "center"
        }
    });

    const svgStyles = css({
        color: "#505258"
    });

    const mobileHeaderStyles = css({
        display: "block",
        textTransform: "uppercase",
        font: "var(--bxp-charlie-text-font-family)",
        fontSize: "14px",
        lineHeight: "18px",
        height: "35px",
        margin: 0,
        borderBlockEnd: "1px solid #6C6F77",
        [media.above.sm]: {
            display: "none"
        }
    });

    const translatedLabel = intl.formatMessage({
        id: "banner-alert.close.aria-label",
        defaultMessage: "Close"
    });

    const [isHidden, setIsHidden] = useState<boolean>(true);

    useEffect(() => {
        const dismissValue = localStorage.getItem(bannerStorageItem);

        if (dismissValue !== "1") {
            setIsHidden(false);
        }
    }, [bannerStorageItem]);

    return (
        <div css={containerWrapperStyles} data-dismissed={isHidden}>
            <div css={backgroundShapeStyles} />
            <div css={containerStyles}>
                <div css={contentWrapperStyles}>
                    <div css={lockupStyles} />
                    <div css={dividerStyles} />
                    <div css={contentStyles}>
                        <p css={mobileHeaderStyles}>{mobileHeaderText}</p>
                        <p css={textStyles}>{text}</p>
                        {linkText && linkUrl && (
                            <Link
                                css={linkStyles}
                                text={linkText}
                                href={linkUrl}
                                type="inline"
                                dataEventProps={dataEventProps}
                            />
                        )}
                    </div>
                </div>
                <div css={fillStyles} />
                <button
                    aria-label={translatedLabel}
                    css={closeIconStyles}
                    onClick={() => {
                        setIsHidden(true);
                        localStorage.setItem(bannerStorageItem, "1");
                    }}
                >
                    <Cross css={svgStyles} size="24" />
                </button>
            </div>
        </div>
    );
};
